import "../style.css";
import { useTheme } from "../contexts/ThemeContext";
import SEOHead from "./SEOHead";

const PrivacyPolicy = () => {
  const { colors } = useTheme();

  return (
    <>
    <SEOHead
        title="Privacy Policy — Collegia: Academics Manager"
        description="Read the privacy policy for Collegia: Academics Manager. Learn how we protect your data with our privacy-first approach. We never sell or share your information."
        keywords="Collegia privacy policy, data protection, student app privacy, Collegia data policy, CUIMS privacy"
        url="https://collegia.app/privacy_policy"
    />
    <div className={`${colors.bg} min-h-screen py-8 px-4`}>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <span className="text-2xl">🔒</span>
          </div>
          <h1 className={`text-4xl md:text-6xl font-bold mb-4 ${colors.text} bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent`}>
            Privacy Policy
          </h1>
          <p className={`text-xl ${colors.textSecondary} font-light mb-2`}>
            For Collegia: Academics Manager
          </p>
          <p className={`${colors.textMuted} text-lg`}>
            Your privacy and data security are our top priorities
          </p>
        </div>

        {/* Introduction */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-500">📋</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Introduction</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  At <span className={`${colors.text} font-semibold`}>Collegia</span>, your privacy is our fundamental priority. 
                  This Privacy Policy explains our privacy-first approach and how our application operates as a 
                  <span className={`${colors.text} font-semibold`}> local-processing interface</span> without storing or transmitting your academic data.
                </p>
                <p>
                  If you have additional questions or require more information about our Privacy Policy, 
                  do not hesitate to <a href="mailto:contact@collegia.app" className="text-blue-400 hover:underline font-semibold">contact us</a>.
                </p>
                <p>
                  This Privacy Policy applies only to our application and is valid regarding 
                  how we handle (or specifically, do NOT handle) your personal and academic information.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Our Privacy-First Approach */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 border-green-500/30 bg-green-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-green-500">🛡️</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Our Privacy-First Architecture</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  <span className={`${colors.text} font-semibold`}>Critical Information:</span> Our application is designed with privacy 
                  as a core principle. Here's what makes our approach unique:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Local Processing Only:</strong> All data processing happens on your device</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>No Data Storage:</strong> We don't store your academic information on our servers</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>No Data Transmission:</strong> Your university data stays on your device</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Interface Layer Only:</strong> We provide a better UI for your own data</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What We Do NOT Collect */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 border-red-500/30 bg-red-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
              <span className="text-red-500">❌</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>What We Do NOT Collect or Store</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  To be completely transparent, here's what we <span className={`${colors.text} font-semibold`}>explicitly do not collect</span>:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Your university credentials or passwords</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Academic records or grades</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Personal academic information</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Course schedules or academic data</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Student ID numbers or personal identifiers</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="text-red-500 text-xl">🚫</span>
                    <span>Any university-specific data or content</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consent Section */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-green-500">✅</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Consent</h2>
              <p className={`${colors.textSecondary} leading-relaxed`}>
                By using our app, you hereby <span className={`${colors.text} font-semibold`}>consent to our Privacy Policy</span> 
                and agree to its terms. You understand that we operate as a presentation interface that processes your 
                academic data locally on your device without storing or transmitting it to our servers.
              </p>
            </div>
          </div>
        </div>

        {/* How Our App Works */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-500">⚙️</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>How Our Application Functions</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  Our application works as a <span className={`${colors.text} font-semibold`}>local interface layer</span>:
                </p>
                <div className="space-y-4 mt-6 ml-4">
                  <div className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">1</span>
                    <div>
                      <strong className={colors.text}>You provide credentials:</strong> You enter your university login details directly on your device
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">2</span>
                    <div>
                      <strong className={colors.text}>Direct connection:</strong> Your device connects directly to the university's system
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">3</span>
                    <div>
                      <strong className={colors.text}>Local processing:</strong> All data is processed and displayed locally on your device
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">4</span>
                    <div>
                      <strong className={colors.text}>No external transmission:</strong> No data leaves your device to reach our servers
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Limited Information Collection */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="text-purple-500">📊</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Limited Information We May Collect</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  The only information we collect is for <span className={`${colors.text} font-semibold`}>app functionality and support</span>:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong className={colors.text}>Google Account Information:</strong> For authentication purposes only (if you choose to sign in with Google)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong className={colors.text}>Contact Information:</strong> Only if you contact us directly for support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong className={colors.text}>Technical Logs:</strong> Basic app performance data (no personal content)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* How We Use Information */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <span className="text-yellow-500">🎯</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>How We Use Your Information</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>We use the limited information we collect only to:</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Provide and maintain our app interface</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>Improve app performance and user experience</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <span>Provide technical support when requested</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <span>Communicate important app updates</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Google Calendar API */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
              <span className="text-red-500">📅</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Google Calendar API Usage</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  If you choose to use Google Calendar integration, you grant permissions to allow our 
                  application to <span className={`${colors.text} font-semibold`}>manage your calendar locally</span>. 
                  This happens entirely on your device - no calendar data is sent to our servers.
                </p>
                <p>
                  Calendar operations include: retrieve, display, edit, delete and manage your calendar 
                  events, all processed locally on your device.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-green-500">🛡️</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Security</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>
                  We value your trust and have designed our application with <span className={`${colors.text} font-semibold`}>security by design</span>:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Local Processing:</strong> Your data never leaves your device</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Secure Authentication:</strong> Using industry-standard OAuth protocols</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>No Data Storage:</strong> No academic information stored on our servers</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Encrypted Connections:</strong> All communications use HTTPS encryption</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Party Policies */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
              <span className="text-orange-500">🔗</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Third Party Privacy Policies</h2>
              <p className={`${colors.textSecondary} leading-relaxed`}>
                Our app's use and transfer of information received from Google APIs 
                adheres to <a 
                  href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" 
                  className="text-blue-400 hover:underline font-semibold"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Services User Data Policy ↗
                </a>, including the Limited Use requirements. We recommend reviewing the privacy policies 
                of any third-party services you choose to integrate with.
              </p>
            </div>
          </div>
        </div>

        {/* Service Providers */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
              <span className="text-indigo-500">🤝</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Service Providers</h2>
              <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                <p>We may employ third-party services for:</p>
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    <span>App hosting and delivery</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>Performance monitoring</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <span>Technical support infrastructure</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Security and compliance monitoring</span>
                  </div>
                </div>
                <p className="mt-4">
                  <span className={`${colors.text} font-semibold`}>Important:</span> These services do not have access to your academic data, 
                  as it never leaves your device. They only handle non-personal technical information necessary for app operation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 bg-gradient-to-r from-blue-500/5 to-purple-500/5`}>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-500">📧</span>
            </div>
            <div>
              <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Contact Us</h2>
              <p className={`${colors.textSecondary} leading-relaxed`}>
                If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at{' '}
                <a 
                  href="mailto:contact@collegia.app" 
                  className="text-green-400 hover:underline font-semibold"
                >
                  contact@collegia.app
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center mt-12 pt-8 border-t border-gray-700/20">
          <div className="flex justify-center space-x-4">
            <a 
              href="/terms_of_service" 
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              📄 Terms of Service
            </a>
            <a 
              href="/university_response" 
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              🤝 Our Response
            </a>
            <a 
              href="/" 
              className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
            >
              🏠 Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const TermsOfService = () => {
    const { colors } = useTheme();

    return (
        <>
        <SEOHead
            title="Terms of Service — Collegia: Academics Manager"
            description="Read the terms of service for Collegia: Academics Manager. Understand the terms and conditions for using the Collegia app."
            keywords="Collegia terms of service, Collegia terms, student app terms, Collegia conditions"
            url="https://collegia.app/terms_of_service"
        />
        <div className={`${colors.bg} min-h-screen py-8 px-4`}>
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-full mb-6">
                        <span className="text-2xl">📄</span>
                    </div>
                    <h1 className={`text-4xl md:text-6xl font-bold mb-4 ${colors.text} bg-gradient-to-r from-green-400 to-blue-600 bg-clip-text text-transparent`}>
                        Terms & Conditions
                    </h1>
                    <p className={`text-xl ${colors.textSecondary} font-light mb-2`}>
                        For Collegia: Academics Manager
                    </p>
                    <p className={`${colors.textMuted} text-lg`}>
                        Please read these terms carefully before using our app
                    </p>
                </div>

                {/* Acceptance of Terms */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <span className="text-green-500">📋</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Acceptance of Terms</h2>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    By downloading or using the app, these terms will <span className={`${colors.text} font-semibold`}>automatically apply to you</span> – 
                                    you should make sure therefore that you read them carefully before using the app.
                                </p>
                                <p>
                                    You're not allowed to copy, modify, reproduce, distribute, publicly display, create derivative works from, decompile, disassemble, reverse engineer or otherwise exploit the app or any portion of it. This prohibition expressly covers, but is not limited to, the application's <span className={`${colors.text} font-semibold`}>features, functionality, user interface (UI), visual design, structure, look-and-feel, layout, graphics, icons, logos, and any underlying source or object code</span>.
                                </p>
                                <p>
                                    Any unauthorized copying constitutes a violation of our intellectual property rights, for which we reserve the right to seek all remedies available under applicable law.
                                </p>
                                <p>
                                    The app itself, together with all trademarks, copyrights, and other intellectual property rights related to it, remain the exclusive property of <span className={`${colors.text} font-semibold`}>Collegia</span>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Service Commitment */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-500">🚀</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Our Service Commitment</h2>
                            <p className={`${colors.textSecondary} leading-relaxed`}>
                                <span className={`${colors.text} font-semibold`}>Collegia</span> is committed to ensuring that the app is as 
                                useful and efficient as possible. For that reason, we reserve the right to make changes to the app or to charge 
                                for its services, at any time and for any reason.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Data Usage & Security */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <span className="text-purple-500">🔐</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Data Usage & Security</h2>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    The application needs the user's credentials of <span className={`${colors.text} font-semibold`}>Your University Information Management System</span> to function. 
                                    The application stores the information provided to us by the user and the data retrieved using the credentials 
                                    provided by the user on the device itself that too in an encrypted <span className={`${colors.text} font-semibold`}>🔒</span> manner.
                                </p>
                                <p>
                                    <span className={`${colors.text} font-semibold`}>Your Responsibility:</span> It's your responsibility to keep your phone and access to the app secure. 
                                    We are taking utmost measures to handle your data in a secure manner.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Third-Party Services */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                            <span className="text-orange-500">🔗</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Third-Party Services</h2>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    The app does use third-party services that declare their own{' '}
                                    <a 
                                        href="https://policies.google.com/terms" 
                                        className="text-blue-400 hover:underline font-semibold"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Terms and Conditions ↗
                                    </a>:
                                </p>
                                <div className="grid md:grid-cols-3 gap-3 mt-4">
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                        <span>Google Play Services</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                        <span>Google Analytics for Firebase</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <span>Google AdMob</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Limitations */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 border-yellow-500/30 bg-yellow-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                            <span className="text-yellow-500">⚠️</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text} text-yellow-400`}>Service Limitations</h2>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    You should be aware that there are certain things that <span className={`${colors.text} font-semibold`}>Collegia will not take responsibility for</span>. 
                                    Certain functions of the app will require the app to have an active internet connection.
                                </p>
                                <p>
                                    The connection can be Wi-Fi, or provided by your mobile network provider, but Collegia cannot take responsibility 
                                    for the app not working at full functionality if you don't have access to Wi-Fi, and you don't have any of your data allowance left.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Changes to Terms */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
                            <span className="text-indigo-500">🔄</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Changes to These Terms</h2>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    We may <span className={`${colors.text} font-semibold`}>update our Terms and Conditions</span> from time to time. 
                                    Thus, you are advised to review this page periodically for any changes.
                                </p>
                                <p>
                                    We will notify you of any changes by posting the new Terms and Conditions on this page.
                                </p>
                                <div className={`${colors.card} rounded-lg p-4 mt-4 bg-gray-500/10`}>
                                    <p className="text-sm">
                                        <span className={`${colors.text} font-semibold`}>Effective Date:</span> These terms and conditions are effective as of <span className="font-mono">2021-09-28</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mb-8 bg-gradient-to-r from-green-500/5 to-blue-500/5`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <span className="text-green-500">📧</span>
                        </div>
                        <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>Contact Us</h2>
                            <p className={`${colors.textSecondary} leading-relaxed`}>
                                If you have any questions or suggestions about our Terms and Conditions, do not hesitate to contact us at{' '}
                                <a 
                                    href="mailto:contact@collegia.app" 
                                    className="text-green-400 hover:underline font-semibold"
                                >
                                    contact@collegia.app
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="text-center mt-12 pt-8 border-t border-gray-700/20">
                    <div className="flex justify-center space-x-4">
                        <a 
                            href="/privacy_policy" 
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🔒 Privacy Policy
                        </a>
                        <a 
                            href="/" 
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🏠 Back to Home
                        </a>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export {PrivacyPolicy, TermsOfService}
